import { z } from "zod";
export const chatChannelSchema = z.enum(["human", "ai"]);
export const chatThreadStatusSchema = z.enum(["open", "resolved"]);
export const chatAuthorTypeSchema = z.enum(["client", "staff", "assistant", "system"]);
export const textBlockSchema = z.object({ type: z.literal("text"), text: z.string().min(1).max(4000) });
// Write schemas: strict. Adding a block type means adding it here.
export const knownBlockSchema = z.discriminatedUnion("type", [textBlockSchema]);
export const staffMessageBodySchema = z.object({ v: z.literal(1), blocks: z.array(knownBlockSchema).min(1).max(20) });
// Server-enforced asymmetry: a client may author exactly one text block.
export const clientMessageBodySchema = z.object({ v: z.literal(1), blocks: z.tuple([textBlockSchema]) });
// Forward-compatibility: the *read* schema must tolerate unknown block types,
// otherwise parseResponse's schema.parse throws on the whole response and an
// old client crashes on a single new block. The renderer's default: case
// never runs because zod rejects first.
export const readBlockSchema = z.union([knownBlockSchema, z.object({ type: z.string() }).passthrough()]);
export const readMessageBodySchema = z.object({ v: z.number(), blocks: z.array(readBlockSchema) });
export const chatThreadSchema = z.object({
    id: z.string(),
    channel: chatChannelSchema,
    status: chatThreadStatusSchema,
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const replyPreviewSchema = z.object({
    id: z.string(),
    authorType: chatAuthorTypeSchema,
    authorName: z.string().nullable(),
    body: readMessageBodySchema,
    redactedAt: z.string().nullable(),
});
export const chatMessageSchema = z.object({
    id: z.string(),
    seq: z.number(),
    authorType: chatAuthorTypeSchema,
    authorId: z.string().nullable(),
    authorName: z.string().nullable(),
    body: readMessageBodySchema,
    // Absent, not null, from an API that predates replies. The apps deploy
    // independently of the API (and a PWA can be serving a cached bundle), so a
    // read schema that *requires* the newest field fails the whole message list
    // — the thread renders empty — until the API catches up.
    replyTo: replyPreviewSchema.nullable().default(null),
    createdAt: z.string(),
    redactedAt: z.string().nullable(),
});
// Wire shape shared by both send endpoints. The route handlers re-validate
// `body` with clientMessageBodySchema or staffMessageBodySchema depending on
// caller — that's where the asymmetry is actually enforced (see api Phase 2).
// staffMessageBodySchema is the superset here since a single-text-block
// client body also satisfies it.
export const sendMessageRequestSchema = z.object({
    clientMessageId: z.string().min(1).max(64),
    body: staffMessageBodySchema,
    replyToId: z.string().uuid().optional(),
});
export const chatMessagePageSchema = z.object({
    messages: z.array(chatMessageSchema),
    lastSeq: z.number(),
});
export const chatSummarySchema = z.object({
    threadId: z.string(),
    status: chatThreadStatusSchema,
    lastSeq: z.number(),
    lastMessageAt: z.string().nullable(),
    unreadCount: z.number(),
    awaitingReplySince: z.string().nullable(),
});
export const waitingOnClientSchema = z.object({
    replyBy: z.string(),
    sentAt: z.string(),
    setByName: z.string().nullable(),
    nudgedAt: z.string().nullable(),
});
export const clinicThreadSummarySchema = chatSummarySchema.extend({
    patientId: z.string(),
    waitingOnClient: waitingOnClientSchema.nullable(),
});
// Deeplink target route contract, defined now so a future `result_ref` block
// (`{ type: "result_ref", resultId, snapshot: {...} }`) has a target to point
// at when it arrives — the block type itself is not added in this phase.
export const RESULT_DEEPLINK_PATH = (id) => `/results/${id}`;
